import { queryOptions, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { authenticatedRequest } from '@/services';
import { HttpMethod } from '@/lib/enums';
import { isUuid } from '@/lib/utils';

export interface CampaignSummary {
  id: string;
  name: string;
  status: string;
  isArchived: boolean;
  dealReference: string | null;
  /** The owning client account. The wizard's on-behalf bar shows it. */
  tenantName?: string | null;
  /** The owning client account's id (NPJB-789 D26) — SI opens on it for an internal user. */
  tenantId?: string | null;
}

/**
 * Checks that a `GET /campaigns/:id` reply is the campaign we asked for (NPJB-837).
 *
 * Some bad ids still get a 200 with a body that is not a campaign. If the body has no `id`,
 * or a different `id`, we throw a 404-shaped error. `isNotFoundError` reads the `status`,
 * so the page shows its not-found panel.
 */
export const parseCampaignSummary = (result: unknown, campaignId: string): CampaignSummary => {
  const id = (result as { id?: unknown } | null | undefined)?.id;
  if (typeof id !== 'string' || id.toLowerCase() !== campaignId.toLowerCase()) {
    throw Object.assign(new Error('Campaign not found'), { status: 404 });
  }
  return result as CampaignSummary;
};

/**
 * The one way to read `queryKeys.campaigns.detail` (NPJB-837).
 *
 * The campaign page and the wizard breadcrumb share this cache entry. So both must fetch it
 * the same way: a bad id never fetches, and an unchecked body never lands in the cache.
 */
export const campaignSummaryQueryOptions = (campaignId: string | undefined) =>
  queryOptions({
    queryKey: queryKeys.campaigns.detail(campaignId ?? ''),
    queryFn: async () => {
      const result = await authenticatedRequest({
        resource: `campaigns/${campaignId}`,
        method: HttpMethod.GET,
      });
      return parseCampaignSummary(result, campaignId ?? '');
    },
    enabled: isUuid(campaignId),
  });

/** One campaign's summary. `staleTime` is the caller's. */
export const useCampaignSummaryQuery = (
  campaignId: string | undefined,
  options: { staleTime?: number } = {},
) => useQuery({ ...campaignSummaryQueryOptions(campaignId), ...options });
